import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  IAuditoriaSplitDespesasResultado,
  IAuditoriaSplitDespesasGrupo,
  IAuditoriaSplitDespesasContaAnalisada,
  IAuditoriaSplitDespesasContaIgnorada,
} from '../interfaces/IAuditoriaSplitDespesas';

interface Props {
  resultado: IAuditoriaSplitDespesasResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'          ? 'badge-light-danger' :
    status === 'ALERTA'        ? 'badge-light-warning' :
    status === 'AVISO'         ? 'badge-light-warning' :
    status === 'NAO_ANALISADO' ? 'badge-light-secondary' :
                                 'badge-light-success';
  const label = status === 'NAO_ANALISADO' ? 'NÃO ANALISADO' : status;
  return <span className={`badge ${cls}`}>{label}</span>;
};

const GrupoExpandivel: React.FC<{ grupo: IAuditoriaSplitDespesasGrupo }> = ({ grupo }) => {
  const [expandido, setExpandido] = useState(false);

  // COR-243/244: rebaixamento automático — status AVISO por cruzar competência ou não atingir
  // a materialidade absoluta mínima, distinto de um AVISO "natural" (percentual baixo).
  const requerValidacaoManual =
    grupo.status === 'AVISO' && (grupo.cruzaCompetencia || !grupo.atingiuMaterialidadeAbsoluta);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{grupo.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{grupo.cnpj}</span>
        </td>
        <td>
          {/* COR-245: conta contábil ECD (primária) separada do referencial RFB (secundário) */}
          <span className='fw-bold'>{grupo.contaContabilEcd} - {grupo.descricaoConta}</span>
          <br />
          <span className='text-muted fs-8'>Ref. RFB: {grupo.contaReferencial || '—'}</span>
        </td>
        <td className='fs-8' style={{ maxWidth: '180px', wordBreak: 'break-word' }}>
          {grupo.fornecedorHistorico}
        </td>
        <td className='text-center fw-bold'>{grupo.quantidadeLancamentos}</td>
        <td className='text-muted fs-8'>{grupo.periodoOcorrencia}</td>
        <td className='fw-bold'>{formatCurrency(grupo.valorIndividualMedio)}</td>
        <td className='fw-bold text-danger'>{formatCurrency(grupo.valorTotalFragmentado)}</td>
        <td className='text-center'>
          <StatusBadge status={grupo.status} />
          {requerValidacaoManual && (
            <div className='fs-9 text-muted mt-1'>Requer validação</div>
          )}
        </td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={9} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2 fs-7'>{grupo.mensagemDiagnostico}</p>
            </div>

            <div className='mb-3'>
              <strong>Memória de Cálculo:</strong>
              <p className='text-muted mt-1 mb-2 fs-7'>{grupo.memoriaCalculo}</p>
            </div>

            {/* Lançamentos individuais do split */}
            <div className='mb-4'>
              <strong className='fs-7'>Lançamentos detectados:</strong>
              <div className='table-responsive mt-2'>
                <table className='table table-sm table-row-gray-200 align-middle gs-0'>
                  <thead>
                    <tr className='fw-bold text-muted fs-8'>
                      <th>Data</th>
                      <th>Lanç. ECD</th>
                      <th>Competência</th>
                      <th>Histórico</th>
                      <th className='text-end'>Valor</th>
                      <th className='text-center'>Tipo</th>
                      <th>Contrapartida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.lancamentos.map((l, i) => (
                      <tr key={i}>
                        <td className='fs-8'>{formatDate(l.dataLancamento)}</td>
                        <td className='fs-8 text-muted'>{l.numeroLancamento || '—'}</td>
                        <td className='fs-8 text-muted'>{l.competencia}</td>
                        <td className='fs-8 text-muted'>{l.historico}</td>
                        <td className='text-end fw-bold'>{formatCurrency(l.valor)}</td>
                        <td className='text-center'>
                          <span className={`badge badge-light-${l.tipoLancamento === 'D' ? 'danger' : 'success'}`}>
                            {l.tipoLancamento}
                          </span>
                        </td>
                        <td className='fs-8 text-muted'>{l.contaContrapartida}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {grupo.status === 'ERRO' && (
              <>
                <div className='row mb-4'>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-6 fw-bolder text-muted'>{formatCurrency(grupo.valorTotalFragmentado)}</div>
                    <div className='text-muted fs-8'>Base de Cálculo</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-6 fw-bolder text-danger'>{formatCurrency(grupo.tributosIrpjCsll)}</div>
                    <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-6 fw-bolder text-danger'>{formatCurrency(grupo.tributoPisCofins)}</div>
                    <div className='text-muted fs-8'>PIS/COFINS (9,25%)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-6 fw-bolder text-danger'>{formatCurrency(grupo.multaArt44)}</div>
                    <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                  </div>
                  <div className='col-2 text-center border-end'>
                    <div className='fs-6 fw-bolder text-warning'>{formatCurrency(grupo.jurosSelic)}</div>
                    <div className='text-muted fs-8'>Juros SELIC</div>
                  </div>
                  <div className='col-2 text-center'>
                    <div className='fs-5 fw-bolder text-danger'>{formatCurrency(grupo.passivoTributario)}</div>
                    <div className='text-muted fs-8'>Passivo Total</div>
                  </div>
                </div>

                <div className='alert alert-danger mt-3 py-2 fs-8'>
                  <strong>Validação documental obrigatória:</strong> indício material de possível
                  fragmentação —{' '}
                  {grupo.percentualSobreMedia.toFixed(2)}% da despesa média mensal (
                  {formatCurrency(grupo.despesaMediaMensal)}). Indício de possível contorno de
                  alçada ou ocultação de valor — a conclusão definitiva depende de validação
                  documental (nota fiscal, contrato, pedido, aprovação interna e política de
                  alçada, quando aplicável); a ECD, isoladamente, não comprova irregularidade. CPC
                  00 (R2) — essência sobre a forma. Lei 9.430/96 Art. 44 I.
                </div>
              </>
            )}

            {grupo.status === 'ALERTA' && (
              <div className='alert alert-warning mt-3 py-2 fs-8'>
                <strong>Ação recomendada:</strong> Indício de split —{' '}
                {grupo.percentualSobreMedia.toFixed(2)}% da despesa média mensal (
                {formatCurrency(grupo.despesaMediaMensal)}). Verifique a essência econômica e
                certifique-se de que há nota fiscal ou contrato cobrindo cada lançamento. CPC 00 (R2).
              </div>
            )}

            {grupo.status === 'AVISO' && requerValidacaoManual && (
              <div className='alert alert-secondary mt-3 py-2 fs-8'>
                <strong>Requer validação manual:</strong> classificação automática rebaixada para AVISO
                {grupo.cruzaCompetencia && ' porque os lançamentos pertencem a competências (mês/ano) diferentes — pode ser provisão de uma competência seguida da baixa/nova provisão da competência seguinte, próximo ao fechamento de mês, não fragmentação'}
                {grupo.cruzaCompetencia && !grupo.atingiuMaterialidadeAbsoluta && ' e '}
                {!grupo.atingiuMaterialidadeAbsoluta && ' porque o valor total está abaixo da materialidade mínima (R$ 100,00)'}
                . Sem passivo fiscal automático — valide a competência e a natureza de cada lançamento
                (ver Memória de Cálculo) antes de concluir.
              </div>
            )}

            {grupo.status === 'AVISO' && !requerValidacaoManual && (
              <div className='alert alert-warning mt-3 py-2 fs-8'>
                <strong>Revisão sugerida:</strong> Padrão recorrente de pagamentos similares —{' '}
                {grupo.percentualSobreMedia.toFixed(2)}% da despesa média mensal. Verifique se são
                despesas recorrentes legítimas (aluguel, assinatura) ou fragmentação intencional.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// COR-266: memória de contas — linha combinada (analisada + ignorada) para exportação, pedida
// explicitamente no ticket ("conta contábil, descrição, referencial, quantidade de lançamentos,
// valor total debitado e status da análise" / "motivo da exclusão").
interface LinhaExportacaoMemoriaContas {
  Empresa: string;
  Tipo: string; // 'Analisada' | 'Ignorada'
  'Conta Contábil': string;
  'Descrição da Conta': string;
  Referencial: string;
  'Qtd. Lançamentos (débito)': string;
  'Valor Total Debitado (R$)': string;
  'Status / Motivo': string;
}

const montarLinhasMemoriaContas = (
  analisadas: IAuditoriaSplitDespesasContaAnalisada[],
  ignoradas: IAuditoriaSplitDespesasContaIgnorada[]
): LinhaExportacaoMemoriaContas[] => [
  ...analisadas.map(c => ({
    Empresa: c.empresaNome,
    Tipo: 'Analisada',
    'Conta Contábil': c.contaContabilEcd,
    'Descrição da Conta': c.descricaoContaContabilEcd,
    Referencial: c.contaReferencial || '—',
    'Qtd. Lançamentos (débito)': String(c.quantidadeLancamentosDebito),
    'Valor Total Debitado (R$)': c.valorTotalDebitado.toFixed(2).replace('.', ','),
    'Status / Motivo': c.status === 'COM_OCORRENCIA' ? 'Com ocorrência' : 'Sem padrão identificado',
  })),
  ...ignoradas.map(c => ({
    Empresa: c.empresaNome,
    Tipo: 'Ignorada',
    'Conta Contábil': c.contaContabilEcd,
    'Descrição da Conta': c.descricaoContaContabilEcd,
    Referencial: c.contaReferencial || '(sem referencial mapeado)',
    'Qtd. Lançamentos (débito)': '0',
    'Valor Total Debitado (R$)': '0,00',
    'Status / Motivo': c.motivo,
  })),
];

const exportarCSVMemoriaContas = (linhas: LinhaExportacaoMemoriaContas[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const cabecalho = Object.keys(linhas[0]);
  const corpo = linhas.map(linha => Object.values(linha));
  const csv = [cabecalho, ...corpo]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${nomeArquivo}.csv`);
};

const exportarXLSXMemoriaContas = (linhas: LinhaExportacaoMemoriaContas[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(linhas);
  const wb = { Sheets: { 'Memória de Contas': ws }, SheetNames: ['Memória de Contas'] };
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, `${nomeArquivo}.xlsx`);
};

// COR-266: "Memória de Contas Analisadas" e "Contas Ignoradas" — permite validar se um resultado
// "0 ocorrências" é real ou fruto de escopo incompleto (o problema relatado nos tickets originais).
const MemoriaDeContas: React.FC<{
  contasAnalisadas: IAuditoriaSplitDespesasContaAnalisada[];
  contasIgnoradas: IAuditoriaSplitDespesasContaIgnorada[];
}> = ({ contasAnalisadas, contasIgnoradas }) => {
  const [mostrar, setMostrar] = useState(false);
  const linhas = montarLinhasMemoriaContas(contasAnalisadas, contasIgnoradas);

  if (contasAnalisadas.length === 0 && contasIgnoradas.length === 0) return null;

  return (
    <div className='mt-5'>
      <button
        type='button'
        className='btn btn-sm btn-light-primary mb-3'
        onClick={() => setMostrar(!mostrar)}
      >
        {mostrar ? 'Ocultar' : 'Exibir'} Memória de Contas ({contasAnalisadas.length} analisada(s),{' '}
        {contasIgnoradas.length} ignorada(s))
      </button>

      {mostrar && (
        <>
          <div className='d-flex gap-2 mb-3'>
            <button
              type='button'
              className='btn btn-sm btn-outline-primary'
              onClick={() => exportarCSVMemoriaContas(linhas, 'memoria_contas_split_despesas')}
            >
              Exportar CSV
            </button>
            <button
              type='button'
              className='btn btn-sm btn-outline-primary'
              onClick={() => exportarXLSXMemoriaContas(linhas, 'memoria_contas_split_despesas')}
            >
              Exportar XLSX
            </button>
          </div>

          <div className='fw-bold fs-7 mb-2'>Contas Analisadas</div>
          <div className='table-responsive mb-5'>
            <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta</th>
                  <th>Referencial</th>
                  <th className='text-center'>Qtd. Lançamentos</th>
                  <th className='text-end'>Valor Total Debitado</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasAnalisadas.length === 0 ? (
                  <tr><td colSpan={6} className='text-muted fst-italic'>Nenhuma conta analisada.</td></tr>
                ) : contasAnalisadas.map((c, i) => (
                  <tr key={i}>
                    <td>{c.empresaNome}</td>
                    <td>{c.contaContabilEcd} - {c.descricaoContaContabilEcd}</td>
                    <td className='text-muted'>{c.contaReferencial || '—'}</td>
                    <td className='text-center'>{c.quantidadeLancamentosDebito}</td>
                    <td className='text-end'>{formatCurrency(c.valorTotalDebitado)}</td>
                    <td className='text-center'>
                      <span className={`badge ${c.status === 'COM_OCORRENCIA' ? 'badge-light-warning' : 'badge-light-success'}`}>
                        {c.status === 'COM_OCORRENCIA' ? 'Com Ocorrência' : 'Sem Padrão Identificado'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='fw-bold fs-7 mb-2'>Contas Ignoradas</div>
          <div className='table-responsive'>
            <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta</th>
                  <th>Referencial</th>
                  <th>Motivo da Exclusão</th>
                </tr>
              </thead>
              <tbody>
                {contasIgnoradas.length === 0 ? (
                  <tr><td colSpan={4} className='text-muted fst-italic'>Nenhuma conta ignorada.</td></tr>
                ) : contasIgnoradas.map((c, i) => (
                  <tr key={i}>
                    <td>{c.empresaNome}</td>
                    <td>{c.contaContabilEcd} - {c.descricaoContaContabilEcd}</td>
                    <td className='text-muted'>{c.contaReferencial || '(sem referencial mapeado)'}</td>
                    <td className='text-muted'>{c.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const AuditoriaSplitDespesasDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'          ? 'danger' :
    resultado.status === 'ALERTA'        ? 'warning' :
    resultado.status === 'AVISO'         ? 'warning' :
    resultado.status === 'NAO_ANALISADO' ? 'secondary' :
                                           'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Fragmentação de Despesas ("Split" Contábil) — Detalhamento
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            CPC 00 (R2) · Lei 6.404/76 Art. 177 · Lei 9.430/96 Art. 44 I · Compliance/SOX
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status === 'NAO_ANALISADO' ? 'NÃO ANALISADO' : resultado.status}:</strong>{' '}
            {resultado.mensagemDirecionamento}
          </div>
        )}

        {/* COR-204: transparência do escopo efetivamente analisado — contas, lançamentos,
            referenciais e critérios utilizados na detecção de split contábil. */}
        {resultado.status !== 'NAO_ANALISADO' && (
          <div className='card bg-light-secondary border-0 mb-5'>
            <div className='card-body py-3'>
              <div className='row'>
                <div className='col-md-3 text-center border-end'>
                  <div className='fs-3 fw-bolder'>{resultado.quantidadeContasAnalisadas}</div>
                  <div className='text-muted fs-8'>Conta(s) Analisada(s)</div>
                </div>
                <div className='col-md-3 text-center border-end'>
                  <div className='fs-3 fw-bolder'>{resultado.quantidadeLancamentosAnalisados}</div>
                  <div className='text-muted fs-8'>Lançamento(s) Avaliado(s)</div>
                </div>
                <div className='col-md-6'>
                  <div className='text-muted fs-8 mb-1'>Referenciais Considerados</div>
                  <div className='fs-8'>
                    {resultado.referenciaisConsiderados.length > 0
                      ? resultado.referenciaisConsiderados.join(', ')
                      : '—'}
                  </div>
                </div>
              </div>
              {resultado.criteriosAnalise && (
                <div className='mt-3 pt-3 border-top'>
                  <div className='text-muted fs-8 mb-1'>Critérios de Detecção</div>
                  <div className='fs-8'>{resultado.criteriosAnalise}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* COR-266: memória de contas — permite validar se um resultado "0 ocorrências" é real ou
            fruto de escopo incompleto (contas fora do referencial monitorado ou sem lançamento
            de débito no período). */}
        {resultado.status !== 'NAO_ANALISADO' && (
          <MemoriaDeContas
            contasAnalisadas={resultado.contasAnalisadas}
            contasIgnoradas={resultado.contasIgnoradas}
          />
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (&gt; 5 %)</div>
            <div className='text-muted fs-8'>Indício material de possível fragmentação</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (1–5 %)</div>
            <div className='text-muted fs-8'>Indício de contorno de alçada</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (&lt; 1 %)</div>
            <div className='text-muted fs-8'>Padrão recorrente — revisar</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>
              {formatCurrency(resultado.passivoTributarioTotal)}
            </div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>IRPJ/CSLL 34% + PIS/COFINS 9,25% + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.grupos.length === 0 ? (
          resultado.status === 'NAO_ANALISADO' ? (
            <div className='text-muted fw-bold fs-5 text-center py-8'>
              ⚠ Nenhuma conta do escopo monitorado foi localizada nesta ECD — ver mensagem acima.
              Esta auditoria não foi analisada, não é uma confirmação de ausência de fragmentação.
            </div>
          ) : (
            <div className='text-success fw-bold fs-5 text-center py-8'>
              ✓ Nenhum padrão de fragmentação detectado em {resultado.quantidadeContasAnalisadas} conta(s)
              analisada(s) ({resultado.quantidadeLancamentosAnalisados} lançamento(s) avaliados).
            </div>
          )
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta</th>
                  <th>Fornecedor / Histórico</th>
                  <th className='text-center'>Qtd.</th>
                  <th>Período</th>
                  <th>Valor Médio</th>
                  <th>Total Split</th>
                  <th className='text-center'>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.grupos.map((g, i) => (
                  <GrupoExpandivel key={i} grupo={g} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaSplitDespesasDetalhe;
